import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import type { GetPersonalizationQueryResult } from "#types/types.ts";
import { useCallback } from "react";
import type { UpdatePersonalizationForm } from "./useUpdatePersonalizationForm.ts";

export const usePersonalizationTracking = () => {
    const { trackEvent } = useTracking();

    const trackPersonalizationChanges = useCallback((
        data: UpdatePersonalizationForm,
        personalization: GetPersonalizationQueryResult
    ) => {
        // Track personality changes
        if (data.personality !== personalization.personality) {
            trackEvent(MixpanelEvent.AssistantPersonalityUpdated, {
                Location: "Personalization Form"
            });
        }

        // Track conversation sharing changes
        if (data.trackingOptOut !== undefined && data.trackingOptOut !== personalization.trackingOptOut) {
            trackEvent(MixpanelEvent.ButtonClicked, {
                Location: "Personalization Form",
                Trigger: "Toggle Debugging",
                "Debugging Enabled": !data.trackingOptOut
            });
        }

        // Track memory changes
        let updatedCount = 0;
        let deletedCount = 0;

        // Check for deleted memories (memories that exist in original but not in new data)
        personalization.memories.forEach(originalMemory => {
            const newMemory = data.memories.find(m => m.memoryId === originalMemory.id);
            if (!newMemory) {
                deletedCount++;
            }
        });

        // Check for updated memories and memories set to empty
        data.memories.forEach(memory => {
            const originalMemory = personalization.memories.find(m => m.id === memory.memoryId);

            if (!originalMemory || originalMemory.value !== memory.value) {
                if (memory.value.trim() === "") {
                    deletedCount++;
                } else {
                    updatedCount++;
                }
            }
        });

        // Calculate remaining memories (used for both events)
        const remainingMemories = data.memories.filter(memory => memory.value.trim() !== "").length;

        // Fire tracking events for memory changes
        if (updatedCount > 0) {
            trackEvent(MixpanelEvent.AssistantMemoryUpdated, {
                Location: "Personalization Form",
                "Remaining Memories": remainingMemories
            });
        }

        if (deletedCount > 0) {
            trackEvent(MixpanelEvent.AssistantMemoryDeleted, {
                Location: "Personalization Form",
                "Remaining Memories": remainingMemories
            });
        }
    }, [trackEvent]);

    return { trackPersonalizationChanges };
};
